/**
 * Language Selector Component
 *
 * Dropdown rendered via portal at body level to avoid z-index / overflow issues.
 * Click button to open, click language to select and close.
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { apiClient } from '../services/api';

const languages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇹🇳', rtl: true },
];

const LanguageSelector = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);

  // Recompute position whenever dropdown opens
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('click', handle);
    return () => document.removeEventListener('click', handle);
  }, [open]);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    // Marks this as a deliberate pick — geo-based auto-detection on the
    // landing page (see hooks/useGeoLanguage.js) must never override it.
    localStorage.setItem('i18nextLngExplicit', '1');
    setOpen(false);
    const isRTL = langCode === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
    // Update cached user_data so useCompanyInfo doesn't override on next load
    try {
      for (const storage of [localStorage, sessionStorage]) {
        const raw = storage.getItem('user_data');
        if (raw) {
          const u = JSON.parse(raw);
          u.preferred_language = langCode;
          storage.setItem('user_data', JSON.stringify(u));
        }
      }
    } catch (_) {}
    // Persist to backend when authenticated (fire-and-forget)
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (token) {
        apiClient.patch('v1/auth/me/language', { language: langCode }).catch(() => {});
      }
    } catch (_) {}
  };

  const currentLanguageCode = i18n.resolvedLanguage || i18n.language?.split('-')[0] || 'fr';
  const currentLang = languages.find((l) => l.code === currentLanguageCode) || languages[0];

  const dropdown = createPortal(
    <div
      style={{
        position: 'absolute', top: dropdownPos.top, right: dropdownPos.right, zIndex: 99999,
        backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)", borderRadius: "10px",
        overflow: "hidden", width: "152px",
      }}
    >
      {languages.map((lang) => (
        <button
          key={lang.code}
          type="button"
          onClick={() => handleLanguageChange(lang.code)}
          style={{
            width: "100%", padding: "8px 12px", fontSize: "13px", textAlign: "left",
            display: "flex", alignItems: "center", gap: "8px",
            backgroundColor: currentLanguageCode === lang.code ? "rgba(94,106,210,0.1)" : "transparent",
            color: currentLanguageCode === lang.code ? "#828fff" : "var(--lm-ink-2)",
            border: "none", cursor: "pointer", transition: "background-color 0.15s",
          }}
          onMouseEnter={e => { if (currentLanguageCode !== lang.code) e.currentTarget.style.backgroundColor = "var(--lm-s3)"; }}
          onMouseLeave={e => { if (currentLanguageCode !== lang.code) e.currentTarget.style.backgroundColor = "transparent"; }}
          dir={lang.rtl ? 'rtl' : 'ltr'}
        >
          <span>{lang.flag}</span>
          <span>{lang.name}</span>
        </button>
      ))}
    </div>,
    document.body
  );

  if (variant === 'minimal') {
    return (
      <>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 px-2 py-2 rounded-lg transition-colors"
          style={{ color: "var(--lm-ink-3)" }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "var(--lm-s2)"; e.currentTarget.style.color = "var(--lm-ink-2)"; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "var(--lm-ink-3)"; }}
          aria-label="Change language"
        >
          <Globe className="w-4 h-4" />
          <span style={{ fontSize: "13px" }}>{currentLang.flag}</span>
        </button>
        {open && dropdown}
      </>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors"
        style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)", color: "var(--lm-ink-2)" }}
        onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--lm-border-2)")}
        onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--lm-border)")}
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span style={{ fontSize: "13px" }}>{currentLang.flag} {currentLang.name}</span>
      </button>
      {open && dropdown}
    </>
  );
};

export default LanguageSelector;
